








import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { merge as deepObjectMerge } from 'lodash';
import { DxChart } from 'devextreme-vue/chart';
import resize from 'vue-resize-directive';

import { DataSource } from '@/types';
import {
    Chart,
    ChartConfig,
    ChartSeries,
    SeriesColorScheme,
} from './';

// Array of auto assignable colors for series
// first element is our primary colors.
// Granted colorScheme depends on indexInSeriesArray
const seriesColorScheme: SeriesColorScheme[] = [
    {
        color: '#acf',
        hoverColor: '#58f',
    }, {
        color: '#F4D341',
        hoverColor: '#D9C742'
    }, {
        color: '#62B98B',
        hoverColor: '#45895F',
    },
];

@Component({
    components: {
        DxChart,
    },
    directives: {
        resize,
    },
})
export default class ChartComponent extends Vue {
    // Chart data
    @Prop({ type: [Object, Array], required: true }) public dataSource!: DataSource;
    @Prop({ type: [Object, Array], required: true }) public series!: ChartSeries | Array<ChartSeries>;
    // ChartConfig object will be deep merged with with local computedChartConfig.
    // * It should describe the behavior of the graph EXCEPT data.
    // * ChartConfig MUST NOT CONTAIN dataSource and series fields:
    // they should be passed separated as props, because
    // we processed them by this.transform... functions, and it will be more clearly.
    @Prop({
        type: Object,
        validator: (config: Partial<ChartConfig>): boolean => {
            const isContainDataSource = config.hasOwnProperty('dataSource');
            const isContainSeries = config.hasOwnProperty('series');

            return !isContainDataSource && !isContainSeries;
        }
    }) public chartConfig?: Partial<ChartConfig>;

    public get computedChartConfig(): ChartConfig {
        // https://js.devexpress.com/Documentation/ApiReference/Data_Visualization_Widgets/dxChart/Configuration/
        const localConfig: Partial<ChartConfig> = {
            dataSource: this.dataSource,
            series: this.transformSeries(this.series),
            valueAxis: {
                maxValueMargin: 0.1,
                minValueMargin: 0.1,
            },
            legend: {
                visible: false,
            },
            adaptiveLayout: {
                keepLabels: false,
                height: 0,
                width: 0,
            },
        };

        return deepObjectMerge(localConfig, this.chartConfig) as ChartConfig;
    }

    public get chartInstance(): Chart {
        // tslint:disable-next-line:no-string-literal
        const element = this.$refs['chart'] as unknown;
        return (element as DxChart).instance!;
    }

    public rerenderOnResize(): void {
        this.chartInstance.render({
            force: true,
            animate: false,
        });
    }

    private transformSeries(
        series: ChartSeries | Array<ChartSeries>,
        indexInSeriesArray: number = 0
    ): ChartSeries | Array<ChartSeries> {
        if (typeof series === 'object') {
            if (Array.isArray(series)) {
                return series.map(
                    // recursive call to handling single chart series by else logic below
                    (singleSeries: ChartSeries, index: number) =>
                        this.transformSeries(singleSeries, index) as ChartSeries
                );
            } else {
                const colorScheme = this.getSeriesColorByIndex(indexInSeriesArray);

                const config: Partial<ChartSeries> = {
                    type: 'bar',
                    color: colorScheme.color,
                    hoverStyle: {
                        color: colorScheme.hoverColor,
                    },
                    label: {
                        visible: true,
                        connector: {
                            visible: true,
                        },
                        font: {
                            color: 'black',
                        }
                    }
                };

                return deepObjectMerge(config, series);
            }
        } else {
            return [] as ChartSeries;
        }
    }

    private getSeriesColorByIndex(index: number): SeriesColorScheme {
        const availableColorsSchemeCount = seriesColorScheme.length;
        while (index > availableColorsSchemeCount) {
            index -= availableColorsSchemeCount;
        }
        return seriesColorScheme[index];
    }
}
